import i18next from 'i18next'
import * as React from 'react'
import { formatDateDDMonthYYYY } from '../../../../utils/date'
import { useInView } from '../../../../utils/hooks/hooks/useInView'
import { Link } from '../../../../utils/links'
import { relatedContentBlockClasses } from '../../../../utils/style'
import { getClassName, slugify } from '../../../../utils/utils'
import { InviewProps } from '../blocksState'

export type RelatedContentBlockStyle = Partial<{
  blockClassName: string
  blockWithHeaderClassName: string
  blockWithLinkClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  introClassName: string
  contentClassName: string
  listClassName: string
  gridItemClassName: string
  gridArticleClassName: string
  gridLinkClassName: string
  gridHeaderClassName: string
  gridContentClassName: string
  gridTitleClassName: string
  gridLabelClassName: string
  gridIntroClassName: string
  gridFauxLinkWrapperClassName: string
  gridFauxLinkClassName: string
  gridFauxLinkLabelClassName: string
  gridVisualClassName: string
  gridImageClassName: string
  gridPictureClassName: string
  gridDateClassName: string
  footerClassName: string
  clickableWrapperClassName: string
  clickableClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  iconId: string
}>

export type RelatedContentItemProps = {
  title: string
  intro?: string
  image?: string
  eventDate?: Date
  url: string
}

type RelatedContentBlockProps = {
  index: number
  inview?: InviewProps
  title?: string
  intro?: string
  teasers: RelatedContentItemProps[]
  readMoreUrl?: string
}

export const RelatedContentBlockLayout = (props: RelatedContentBlockProps) => {
  const { index, inview, intro, teasers, title, readMoreUrl } = props
  const blockWithHeaderClass = title || intro ? ` ${getClassName(relatedContentBlockClasses, 'blockWithHeaderClassName', '')}` : ''
  const blockWithLinkClass = readMoreUrl ? ` ${getClassName(relatedContentBlockClasses, 'blockWithLinkClassName', '')}` : ''
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("RelatedContentBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  const inviewClassName = getClassName(relatedContentBlockClasses, 'blockClassName', 'container') + blockWithHeaderClass + blockWithLinkClass + ' ' + (inView ? getClassName(relatedContentBlockClasses, 'inviewClassName', 'inview') : '')

  return (
    <section key={`block-${index}`} id={`io-${index}`} ref={ref} className={inviewClassName}>
      <div className={getClassName(relatedContentBlockClasses, 'innerClassName', 'py-5')}>
        {(title || intro) && (
          <header className={getClassName(relatedContentBlockClasses, 'headerClassName', '')}>
            {title && <h2 className={getClassName(relatedContentBlockClasses, 'titleClassName', 'mb-3')}>{title}</h2>}
            {intro && <p className={getClassName(relatedContentBlockClasses, 'introClassName', '')}>{intro}</p>}
          </header>
        )}

        {teasers.length > 0 && (
          <div className={getClassName(relatedContentBlockClasses, 'contentClassName', '')}>
            <ul className={getClassName(relatedContentBlockClasses, 'listClassName', 'list-unstyled mb-0 row')}>
              {teasers.map((item) => (
                <RelatedContentItemLayout {...item} key={`rc-${slugify(item.url)}`} />
              ))}
            </ul>
          </div>
        )}

        {readMoreUrl && (
          <footer className={getClassName(relatedContentBlockClasses, 'footerClassName', 'text-center')}>
            <div className={getClassName(relatedContentBlockClasses, 'clickableWrapperClassName', 'd-inline-block')}>
              <Link
                url={readMoreUrl}
                className={getClassName(relatedContentBlockClasses, 'clickableClassName', 'btn btn-dark d-inline-flex')}
              >
                <span className={getClassName(relatedContentBlockClasses, 'clickableLabelClassName', '')}>
                  {i18next.t('misc:viewAllRelatedArticles')}
                </span>
                <svg
                  className={getClassName(relatedContentBlockClasses, 'clickableIconClassName', 'ml-3')}
                  aria-hidden="true"
                  width="20px"
                  height="20px"
                >
                  <use xlinkHref={getClassName(relatedContentBlockClasses, 'iconId', '#icon--chevron-right-large')} />
                </svg>
              </Link>
            </div>
          </footer>
        )}
      </div>
    </section>
  )
}

export const RelatedContentItemLayout = (p: RelatedContentItemProps) => (
  <li className={getClassName(relatedContentBlockClasses, 'gridItemClassName', 'col-md-6 col-lg-4 py-3')}>
    <article className={getClassName(relatedContentBlockClasses, 'gridArticleClassName', '')}>
      <Link
        url={p.url}
        className={getClassName(
          relatedContentBlockClasses,
          'gridLinkClassName',
          'btn btn-link w-100 text-left border-bottom rounded-0 pl-0'
        )}
      >
        <div className={getClassName(relatedContentBlockClasses, 'gridContentClassName', 'card-body px-0 pb-0')}>
          <header className={getClassName(relatedContentBlockClasses, 'gridHeaderClassName', '')}>
            {p.title && (
              <h3 className={getClassName(relatedContentBlockClasses, 'gridTitleClassName', 'mb-2')}>
                {p.title}
                {p.eventDate && <span className="sr-text"> ({formatDateDDMonthYYYY(i18next, p.eventDate || '')})</span>}
              </h3>
            )}
          </header>

          {p.intro && <p className={getClassName(relatedContentBlockClasses, 'gridIntroClassName', '')}>{p.intro}</p>}

          <footer
            className={getClassName(
              relatedContentBlockClasses,
              'gridFauxLinkWrapperClassName',
              'd-inline-flex align-items-center'
            )}
            aria-hidden="true"
          >
            <div className={getClassName(relatedContentBlockClasses, 'gridFauxLinkClassName', 'btn btn-link px-0')}>
              <span className={getClassName(relatedContentBlockClasses, 'gridFauxLinkLabelClassName', '')}>
                {i18next.t('common:readMore')}
              </span>
            </div>
          </footer>
        </div>

        <div className={getClassName(relatedContentBlockClasses, 'gridVisualClassName', 'card-img-top')}>
          {p.image && (
            <img
              data-src={p.image}
              src="/images/empty.png"
              className={getClassName(relatedContentBlockClasses, 'gridImageClassName', 'card-img-top')}
              width="320"
              height="160"
            />
          )}
          {p.eventDate && (
            <time
              className={getClassName(relatedContentBlockClasses, 'gridDateClassName', '')}
              dateTime={p.eventDate.toISOString()}
            >
              <span>
                {p.eventDate.toLocaleDateString('nl-NL', {
                  day: '2-digit',
                })}
              </span>
              <span>
                {p.eventDate.toLocaleDateString('nl-NL', {
                  month: 'short',
                })}
              </span>
            </time>
          )}
        </div>
      </Link>
    </article>
  </li>
)
